import type { WorkspaceItem } from '@/hooks/useWorkspace';
import type { TreeItem, TreeItems } from '@/lib/tree/types';
import { findTopLevelParent } from '@/lib/tree/utilities';
import { api } from '@/lib/trpc/react';
import { useDebouncedCallback } from 'use-debounce';

export const calculateChanges = (
  items: TreeItem[],
  oldChatbots: WorkspaceItem[],
) => {
  const olderChatbots = oldChatbots.filter(
    (chatbot) => chatbot.type !== 'WORKSPACE',
  );

  // Helper function to flatten the tree structure
  const flattenTree = (treeItems: TreeItem[]): TreeItem[] => {
    const result: TreeItem[] = [];

    for (const item of treeItems) {
      result.push({
        ...item,
        teamId: findTopLevelParent(items, item.id)?.teamId ?? null,
      });
      if (item.children && item.children.length > 0) {
        result.push(...flattenTree(item.children));
      }
    }

    return result;
  };

  // Flatten the tree to include all children
  const flattenedItems = flattenTree(items);
  const nonWorkspaceItems = flattenedItems.filter(
    (item) => item.type !== 'WORKSPACE',
  );

  // Extract chatbot data from flattened items and set displayOrder based on position
  const itemChatbots = nonWorkspaceItems.map((item, index) => {
    // Set displayOrder based on position in the array
    const displayOrder = index;

    // Extract the chatbot properties based on item type
    return {
      id: String(item.id),
      displayOrder,
      groupId: (item.parentId as string) ?? null,
      teamId: item.teamId ?? null,
      groupCollapsed: item.collapsed ?? false,
    };
  });

  // Find chatbots that have changed
  const changedChatbots = itemChatbots.filter((current) => {
    const old = olderChatbots.find((c) => c.id === current.id);
    return (
      !old ||
      current.displayOrder !== old.displayOrder ||
      current.teamId !== old.teamId ||
      current.groupId !== old.groupId
    );
  });

  return changedChatbots;
};

const useSortSidebar = () => {
  const utils = api.useUtils();

  const { mutate: serverSortMutate } = api.chatbot.sort.useMutation({
    onError: (err) => {
      console.error('Error sorting sidebar:', err);
    },
  });

  // This function handles both optimistic UI updates and server-side persistence
  const updateSort = (treeItems: TreeItems) => {
    // Call the server mutation
    const oldChatbots = utils.workspace.all.getData()?.entities;

    if (!oldChatbots) return;

    // Calculate changes and send to server
    const changes = calculateChanges(treeItems, oldChatbots);

    utils.workspace.all.setData(undefined, (prev) => {
      if (!prev) return prev;

      const newChatbots = prev.entities.map((chatbot) => {
        const change = changes.find((c) => c.id === chatbot.id);
        if (change) {
          return { ...chatbot, ...change };
        }
        return chatbot;
      });

      return {
        ...prev,
        entities: newChatbots,
      };
    });

    if (changes.length > 0) {
      serverSortMutate(changes);
    }
  };

  const debouncedUpdateSort = useDebouncedCallback(updateSort, 500);

  return { debouncedUpdateSort };
};

export default useSortSidebar;
